import '@/components/proposals/proposal-animations.css';

import { ChevronLeft } from '@untitledui/icons';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AgentFeedContent } from '@/components/proposals/AgentFeedContent';
import { Button } from '@/lib/base/buttons/button';
import { PageLayout } from '@/lib/base/page-layout';

/**
 * Full-page agent activity feed (/actions/agent-feed), navigated to from the
 * AI Actions list's sticky footer. Carries its own title row with a back
 * button returning to wherever the user came from.
 */
export default function AgentFeedPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Hide the mobile nav header while this full-page view is open (same
  // treatment as the action detail page).
  useEffect(() => {
    document.body.setAttribute('data-proposal-open', 'true');
    return () => {
      document.body.removeAttribute('data-proposal-open');
    };
  }, []);

  // Back chevron: return to the list view the user came from (carried in
  // navigation state), falling back to the actions list for deep links.
  // Deliberately NOT navigate(-1): slideout push/replace history entries
  // make history-walking unreliable.
  const backPath = (location.state as { backPath?: string } | null)?.backPath;
  const handleBack = useCallback(() => {
    if (backPath) {
      navigate(backPath);
    } else if (orgId) {
      navigate(`/${orgId}/actions`);
    }
  }, [backPath, navigate, orgId]);

  return (
    <div className='page-enter flex flex-col h-full min-h-0 overflow-hidden bg-bg-primary'>
      <div className='flex-1 min-h-0 flex flex-col'>
        <PageLayout
          title='Agent Feed'
          icon={
            <Button
              color='tertiary'
              size='sm'
              iconLeading={ChevronLeft}
              onClick={handleBack}
              aria-label='Back to actions list'
              className='flex-shrink-0'
            />
          }
        >
          <div className='flex-1 min-h-0 overflow-y-auto bg-[var(--color-bg-secondary)]'>
            <div className='p-3 sm:p-4 lg:p-6 max-w-[48rem] mx-auto w-full'>
              <AgentFeedContent orgId={orgId ? parseInt(orgId) : null} />
            </div>
          </div>
        </PageLayout>
      </div>
    </div>
  );
}
